import { DataTable, type Column } from '@/components/organisms'
import { OrderbookTableContentBaseProps, formatDateDDMMYYYY } from './OrderbookTableContentBase'
import type { OrderbookItem } from '@/types/otc'
import { formatCompactNumber, getOrderTypeVariant } from '@/utils'
import { Badge } from '@/components/ui/badge'

interface OrderbookTableContentDesktopProps extends OrderbookTableContentBaseProps {}

// Get columns definition for desktop table
export const getOrderbookColumns = (): Column<OrderbookItem>[] => [
  {
    key: 'project_name',
    title: 'Project',
    width: 'w-[200px]',
    render: (item: OrderbookItem) => (
      <div className="flex items-center gap-[10px]">
        <div className="relative shrink-0">
          {item.logo ? (
            <img
              src={item.logo}
              alt={item.project_name}
              className="h-[26px] w-[26px] rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.src = '/placeholder-logo.png'
              }}
            />
          ) : (
            <div className="h-[26px] w-[26px] rounded-full bg-[#f5f5f5] dark:bg-[rgba(253,253,253,0.1)] flex items-center justify-center">
              <span className="text-xs font-semibold text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">
                {item.project_name?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
          )}
        </div>
        <span className="font-plus-jakarta font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] overflow-hidden text-ellipsis whitespace-nowrap">
          {item.project_name}
        </span>
      </div>
    ),
    sortable: false
  },
  {
    key: 'type',
    title: 'Type',
    width: 'w-[100px]',
    render: (item: OrderbookItem) => (
      <Badge variant={getOrderTypeVariant(item.type)}>
        {item.type}
      </Badge>
    ),
    sortable: true
  },
  {
    key: 'deal_type',
    title: 'Asset Type',
    width: 'w-[120px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-semibold text-[14px] leading-[20px] text-[#0e6aea] dark:text-[#fdfdfd]">
        {item.deal_type}
      </span>
    ),
    sortable: true
  },
  {
    key: 'offered_amount',
    title: 'Amount',
    width: 'w-[150px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        ${formatCompactNumber(item.offered_amount)}
      </span>
    ),
    sortable: true
  },
  {
    key: 'offered_fully_diluted_value',
    title: 'FDV',
    width: 'w-[150px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        ${formatCompactNumber(item.offered_fully_diluted_value)}
      </span>
    ),
    sortable: true
  },
  {
    key: 'round',
    title: 'Round',
    width: 'w-[120px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {item.round || '－'}
      </span>
    ),
    sortable: true
  },
  {
    key: 'date',
    title: 'Data',
    width: 'w-[120px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {formatDateDDMMYYYY(item.date)}
      </span>
    ),
    sortable: true
  }
]

export const OrderbookTableContentDesktop = ({
  data,
  isLoading = false,
  onRowClick,
  emptyMessage = 'No orders available',
  sortColumn,
  sortDirection,
  onSort
}: OrderbookTableContentDesktopProps) => {
  const columns = getOrderbookColumns()

  return (
    <DataTable
      columns={columns}
      data={data}
      keyExtractor={(item: OrderbookItem) => item.order_id}
      isLoading={isLoading}
      onRowClick={onRowClick}
      emptyMessage={emptyMessage}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={onSort}
    />
  )
}

